// Contrast checker utility for WCAG compliance

// WCAG Threshold Constants
const WCAG_AA_LARGE_TEXT_RATIO = 3;
const WCAG_AA_NORMAL_TEXT_RATIO = 4.5;
const WCAG_AAA_LARGE_TEXT_RATIO = 4.5;
const WCAG_AAA_NORMAL_TEXT_RATIO = 7;
const LARGE_TEXT_FONT_SIZE = 18;
const LARGE_TEXT_FONT_SIZE_BOLD = 14;
const LARGE_TEXT_FONT_WEIGHT = 600;

// Color Calculation Constants
const MAX_RGB_VALUE = 255;
const LUMINANCE_THRESHOLD = 0.03928;
const LUMINANCE_DIVISOR = 12.92;
const LUMINANCE_OFFSET = 0.055;
const LUMINANCE_DIVISOR_2 = 1.055;
const LUMINANCE_GAMMA = 2.4;
const LUMINANCE_R_WEIGHT = 0.2126;
const LUMINANCE_G_WEIGHT = 0.7152;
const LUMINANCE_B_WEIGHT = 0.0722;
const CONTRAST_OFFSET = 0.05;

// Hex Parsing Constants
const HEX_SHORT_LENGTH = 3;
const HEX_FULL_LENGTH = 6;
const HEX_SUBSTRING_LENGTH = 4;

// Default Values
const DEFAULT_ALPHA = 1;

export const ContrastChecker = {
  // Convert hex/rgba to RGB values
  parseColor: (color) => {
    if (!color) return { r: 0, g: 0, b: 0, a: DEFAULT_ALPHA };

    // Convert to string if it's not already
    let colorString = color;
    if (typeof color !== "string") {
      if (color && typeof color === "object") {
        // Handle color objects (e.g., from color pickers)
        if (
          typeof color.r !== "undefined" &&
          typeof color.g !== "undefined" &&
          typeof color.b !== "undefined"
        ) {
          const a = typeof color.a !== "undefined" ? color.a : DEFAULT_ALPHA;
          return {
            r: Math.round(color.r),
            g: Math.round(color.g),
            b: Math.round(color.b),
            a: a
          };
        }
        // Try to convert to string
        colorString = String(color);
      } else {
        colorString = String(color);
      }
    }

    // Ensure it's a valid string before calling match
    if (typeof colorString !== "string") {
      return { r: 0, g: 0, b: 0, a: DEFAULT_ALPHA };
    }

    // Handle rgba format
    const rgbaMatch = colorString.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
    );
    if (rgbaMatch) {
      return {
        r: parseInt(rgbaMatch[1]),
        g: parseInt(rgbaMatch[2]),
        b: parseInt(rgbaMatch[3]),
        a:
          typeof rgbaMatch[4] !== "undefined"
            ? parseFloat(rgbaMatch[4])
            : DEFAULT_ALPHA
      };
    }

    // Handle hex format
    const hex = colorString.replace("#", "");
    if (hex.length === HEX_SHORT_LENGTH) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: DEFAULT_ALPHA
      };
    }
    if (hex.length === HEX_FULL_LENGTH) {
      return {
        r: parseInt(hex.substr(0, 2), 16),
        g: parseInt(hex.substr(2, 2), 16),
        b: parseInt(hex.substr(HEX_SUBSTRING_LENGTH, 2), 16),
        a: DEFAULT_ALPHA
      };
    }

    return { r: 0, g: 0, b: 0, a: DEFAULT_ALPHA };
  },

  // Calculate relative luminance (WCAG formula)
  getLuminance: (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map((val) => {
      const normalizedVal = val / MAX_RGB_VALUE;
      return normalizedVal <= LUMINANCE_THRESHOLD
        ? normalizedVal / LUMINANCE_DIVISOR
        : Math.pow(
            (normalizedVal + LUMINANCE_OFFSET) / LUMINANCE_DIVISOR_2,
            LUMINANCE_GAMMA
          );
    });
    return (
      LUMINANCE_R_WEIGHT * rs +
      LUMINANCE_G_WEIGHT * gs +
      LUMINANCE_B_WEIGHT * bs
    );
  },

  // Blend two colors with alpha
  blendColors: (fgColor, bgColor) => {
    const fg = ContrastChecker.parseColor(fgColor);
    const bg = ContrastChecker.parseColor(bgColor);

    const alpha = fg.a;
    return {
      r: Math.round(fg.r * alpha + bg.r * (1 - alpha)),
      g: Math.round(fg.g * alpha + bg.g * (1 - alpha)),
      b: Math.round(fg.b * alpha + bg.b * (1 - alpha))
    };
  },

  // Calculate contrast ratio
  getContrastRatio: (color1, color2) => {
    const c1 = ContrastChecker.parseColor(color1);
    const c2 = ContrastChecker.parseColor(color2);

    // If color1 has alpha, blend it with color2
    const blended =
      c1.a < DEFAULT_ALPHA ? ContrastChecker.blendColors(color1, color2) : c1;
    const bg =
      c2.a < DEFAULT_ALPHA
        ? ContrastChecker.blendColors(color2, {
            r: MAX_RGB_VALUE,
            g: MAX_RGB_VALUE,
            b: MAX_RGB_VALUE,
            a: DEFAULT_ALPHA
          })
        : c2;

    const lum1 = ContrastChecker.getLuminance(blended.r, blended.g, blended.b);
    const lum2 = ContrastChecker.getLuminance(bg.r, bg.g, bg.b);

    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + CONTRAST_OFFSET) / (darker + CONTRAST_OFFSET);
  },

  // Check WCAG compliance
  checkWCAG: (textColor, backgroundColor, fontSize, fontWeight) => {
    const ratio = ContrastChecker.getContrastRatio(textColor, backgroundColor);

    /*
     * WCAG AA: 4.5:1 for normal text, 3:1 for large text (18pt+ or 14pt+ bold)
     * WCAG AAA: 7:1 for normal text, 4.5:1 for large text
     */
    const isLargeText =
      fontSize >= LARGE_TEXT_FONT_SIZE ||
      (fontSize >= LARGE_TEXT_FONT_SIZE_BOLD &&
        fontWeight >= LARGE_TEXT_FONT_WEIGHT);

    const aaPass = isLargeText
      ? ratio >= WCAG_AA_LARGE_TEXT_RATIO
      : ratio >= WCAG_AA_NORMAL_TEXT_RATIO;
    const aaaPass = isLargeText
      ? ratio >= WCAG_AAA_LARGE_TEXT_RATIO
      : ratio >= WCAG_AAA_NORMAL_TEXT_RATIO;

    return {
      ratio: ratio.toFixed(2),
      aa: aaPass,
      aaa: aaaPass,
      level: aaaPass ? "AAA" : aaPass ? "AA" : "Fail",
      isLargeText: isLargeText
    };
  }
};
